import { POWER_UPS, upgradePrice } from './powerUps';

/**
 * Garage items and prices (pesewas). Shared so the server charges the real price.
 * Buying needs enough money in the wallet: you can't shop while you owe.
 */
export interface Paint {
  id: string;
  name: string;
  price: number;
  body: string;
  /** Bands low → high along the side. */
  stripes: readonly string[];
  /** Special unlocks shown instead of a price. */
  unlock?: 'streak7' | 'march6';
}

export const PAINTS: readonly Paint[] = [
  { id: 'accra-white', name: 'Accra White', price: 0, body: '#f4f4f1', stripes: ['#1d3557', '#457b9d'] },
  { id: 'black-star', name: 'Black Star', price: 60000, body: '#f4f4f1', stripes: ['#ce1126', '#fcd116', '#006b3f'] },
  { id: 'kente-gold', name: 'Kente Gold', price: 90000, body: '#f2c14e', stripes: ['#006b3f', '#1b1b1b', '#ce1126'] },
  { id: 'fish-orange', name: 'Fish Orange (as in the photo)', price: 0, body: '#f7f7f5', stripes: ['#f5a623', '#1b1b1b'] },
  { id: 'osu-night', name: 'Osu Night', price: 140000, body: '#1d1f2b', stripes: ['#ff4d6d', '#ffd166'] },
  { id: 'aburi-green', name: 'Aburi Green', price: 180000, body: '#2d6a4f', stripes: ['#95d5b2', '#fefae0'] },
  { id: 'chrome-hustler', name: 'Chrome Hustler', price: 300000, body: '#c9ced6', stripes: ['#2b2d42', '#8d99ae'] },
  { id: 'ghana-70', name: 'Ghana @ 70', price: 0, body: '#006b3f', stripes: ['#ce1126', '#fcd116'], unlock: 'march6' },
  { id: 'streak-gold', name: 'Streak Gold', price: 0, body: '#e0a800', stripes: ['#1b1b1b', '#fff3b0'], unlock: 'streak7' },
];

export interface Sticker {
  id: string;
  text: string;
  price: number;
}

/** Back-window stickers. The first few are free; the rest cost a little. */
export const STICKERS: readonly Sticker[] = [
  { id: 'god-never-fails', text: 'GOD NEVER FAILS', price: 0 },
  { id: 'sea-never-dry', text: 'SEA NEVER DRY', price: 0 },
  { id: 'obi-nnim-obi', text: 'OBI NNIM OBI', price: 0 },
  { id: 'nyame-nhyira', text: 'NYAME NHYIRA', price: 5000 },
  { id: 'enemies', text: 'ENEMIES ARE NOT HAPPY', price: 5000 },
  { id: 'still-hustling', text: 'STILL HUSTLING', price: 5000 },
  { id: 'fear-women', text: 'FEAR WOMEN', price: 8000 },
  { id: 'no-weapon', text: 'NO WEAPON FORMED', price: 8000 },
];

/** A sticker with your own words (up to 24 characters). */
export const CUSTOM_STICKER_PRICE = 30000;
export const CUSTOM_STICKER_MAX = 24;

export interface Horn {
  id: string;
  name: string;
  blurb: string;
  price: number;
}

export const HORNS: readonly Horn[] = [
  { id: 'trotro-classic', name: 'Trotro Classic', blurb: 'Two tired reeds', price: 0 },
  { id: 'pa-pom', name: 'Pa-pom', blurb: 'Pa-pom, pa-pom!', price: 40000 },
  { id: 'highlife', name: 'Highlife Tune', blurb: 'Every mate knows this one', price: 90000 },
  { id: 'air-horn', name: 'Tanker Air Horn', blurb: 'Everyone moves', price: 150000 },
];

export type GarageKind = 'paint' | 'sticker' | 'horn' | 'upgrade';

export function garagePrice(kind: GarageKind, id: string): number | null {
  if (kind === 'upgrade') {
    const [power, level] = id.split(':');
    return POWER_UPS.some((p) => p.id === power) ? upgradePrice(Number(level)) : null;
  }
  const list = kind === 'paint' ? PAINTS : kind === 'sticker' ? STICKERS : HORNS;
  const item = list.find((i) => i.id === id);
  if (!item || ('unlock' in item && item.unlock)) return null;
  return item.price;
}
